import React, { PureComponent } from 'react';
import { Loader } from 'react-loaders';
import _ from "lodash";
import moment from "moment-timezone";

import {
  batchRequest,
  putRequest,
  postRequest
} from '../../../../api/ApiService';
import Auth from '../../../../routes/Auth';
import { getDateInCurrentTimezone } from "../../../../services/utils";

import {
  Wrapper,
  Title,
  Text,
  DataContainer,
  DataContainerTitle,
  Button,
  HeaderColumn,
  TableCell
} from '../components/ui';

import AbbreviateNumber from "../../../../components/ui-building-blocks/numbers/AbbreviateNumber";
import ErrorAlert from '../../../../components/alerts/error';
import SuccessAlert from '../../../../components/alerts/success';

class TeamScore extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      loaded: false,
      roundTargets: [],
      changedRoundTargets: [],
      // roundsWeights state moved to overall-score (Scoring Weights)
      // along with the Modify Round Weights UI. This page now owns
      // only metrics and per-round targets.
      activeRoundNumbers: [],
      showError: false,
      errorTitle: '',
      errorMessage: '',
      showSuccess: false,
      successMessage: '',
      defaultActiveRoundTargets: [],
    };
  }

  componentDidMount() {
    batchRequest([
      `${process.env.REACT_APP_API_URL}v2/games/targets/?course=${Auth.getCourseId()}`,
      `${process.env.REACT_APP_API_URL}game/rounds/`,
      `${process.env.REACT_APP_API_URL}courses/`,
    ])
      .then(res => {
        const gamesTargets = res[0].body;
        const rounds = res[1].body;
        const courseId = res[2].body[0].id;

        const defaultTargetsData = [
          {
            id: 1,
            course: courseId,
            roundNumber: 1,
            marketShare: 12,
            netRevenues: '4.00',
            grossMargin: 60,
            stockPrice: 1.65,
            roe: 0,
            roa: 0,
            eps: 0,
            roundEndDate: null
          },
          {
            id: 2,
            course: courseId,
            roundNumber: 2,
            marketShare: 15,
            netRevenues: '10.00',
            grossMargin: 55,
            stockPrice: 3,
            roe: 0,
            roa: 0,
            eps: 0,
            roundEndDate: null
          },
          {
            id: 3,
            course: courseId,
            roundNumber: 3,
            marketShare: 0,
            netRevenues: '17.50',
            grossMargin: 55,
            stockPrice: 0,
            roe: 25,
            roa: 25,
            eps: 0,
            roundEndDate: null
          },
        ];

        const isDefaultRoundTargetsActive = gamesTargets.length === 0;
        const isDefaultRoundsActive = rounds.length === 0;

        const roundTargets = isDefaultRoundTargetsActive
          ? defaultTargetsData
          : gamesTargets.map((gamesTarget) => {
            const marketShare = gamesTarget.market_share ? (gamesTarget.market_share * 100).toFixed(2) : 0;
            const netRevenues = gamesTarget.net_revenues ? (gamesTarget.net_revenues / 1000000).toFixed(2) : 0;
            const grossMargin = gamesTarget.gross_margin ? (gamesTarget.gross_margin * 100).toFixed(2) : 0;
            const stockPrice = gamesTarget.stock_price ? (gamesTarget.stock_price).toFixed(2) : 0;
            const roe = gamesTarget.roe ? (gamesTarget.roe * 100).toFixed(2) : 0;
            const roa = gamesTarget.roa ? (gamesTarget.roa * 100).toFixed(2) : 0;
            const eps = gamesTarget.earnings_per_share ? (gamesTarget.earnings_per_share).toFixed(2) : 0;

            const relativeRound = rounds ? rounds.find(round => round.number === gamesTarget.round_number) : null;

            return {
              id: gamesTarget.id,
              course: gamesTarget.course,
              roundNumber: gamesTarget.round_number,
              marketShare,
              netRevenues,
              grossMargin,
              stockPrice,
              roe,
              roa,
              eps,
              roundEndDate: (rounds && relativeRound) ? relativeRound.end_date : null
            }
          });

        // Active round numbers come from the rounds endpoint (or
        // default 1/2/3 when no rounds are configured). Used to
        // filter the targets table and to look up the end date for
        // per-row input gating.
        const activeRoundNumbers = isDefaultRoundsActive
          ? [1, 2, 3]
          : rounds.map((r) => r.number);

        const endDateByRound = {};
        if (!isDefaultRoundsActive) {
          rounds.forEach((r) => {
            endDateByRound[r.number] = r.end_date;
          });
        }

        // Show every target the backend has for this course, even for
        // rounds that don't exist in `game/rounds/` yet. Instructors need
        // visibility into all pre-provisioned targets.
        const roundTargetsFiltered = roundTargets.map((rt) => ({
          ...rt,
          roundEndDate: endDateByRound[rt.roundNumber] || rt.roundEndDate,
        }));

        this.setState({
          roundTargets: roundTargetsFiltered,
          activeRoundNumbers,
          loaded: true,
          defaultActiveRoundTargets: isDefaultRoundTargetsActive ? [1, 2, 3] : [],
        });
      })
      .catch(err => { });
  }

  handleRoundsTargetsInputChange = (e, value, roundNumber, name) => {
    const val = 'floatValue' in value ? value.floatValue : Number(e.target.value);
    let roundTargets = _.cloneDeep(this.state.roundTargets);
    const index = roundTargets.findIndex(i => i.roundNumber === Number(roundNumber));

    if (index >= 0) {
      roundTargets[index][name] = val.toFixed(2);
      let changedRoundTargets = [...this.state.changedRoundTargets];
      if (!changedRoundTargets.includes(roundNumber)) {
        changedRoundTargets.push(roundNumber);
      }

      this.setState({
        roundTargets,
        changedRoundTargets,
      });
    }
  }

  submitRoundTarget = (roundTarget) => {
    const { changedRoundTargets, defaultActiveRoundTargets } = this.state;

    const metricsToCheck = [
      'marketShare',
      'netRevenues',
      'grossMargin',
      'stockPrice',
      'roe',
      'roa',
      'eps'
    ];

    const nonZeroCount = metricsToCheck.filter(metric => {
      const value = parseFloat(roundTarget[metric]);
      return value > 0;
    }).length;

    if (nonZeroCount !== 4) {
      this.setState({
        showError: true,
        errorTitle: 'Error | Invalid Metrics',
        errorMessage: 'Unsuccessful. Make sure exactly 4 metrics have values greater than zero.'
      });
      return;
    }

    const apiUrl = `${process.env.REACT_APP_API_URL}v2/games/targets/`;
    const request = defaultActiveRoundTargets.includes(roundTarget.roundNumber) ? postRequest(`${apiUrl}?round_number=${roundTarget.roundNumber}/`) : putRequest(`${apiUrl}${roundTarget.id}/?round_number=${roundTarget.roundNumber}/`);

    request.send({
      course: roundTarget.course,
      round_number: roundTarget.roundNumber,
      market_share: roundTarget.marketShare / 100,
      net_revenues: roundTarget.netRevenues * 1000000,
      gross_margin: roundTarget.grossMargin / 100,
      stock_price: roundTarget.stockPrice,
      roe: roundTarget.roe / 100,
      roa: roundTarget.roa / 100,
      earnings_per_share: roundTarget.eps
    })
      .then(() => {
        this.setState({
          showSuccess: true,
          successMessage: `Round ${roundTarget.roundNumber} targets are updated.`,
          changedRoundTargets: changedRoundTargets.filter(i => i !== roundTarget.roundNumber),
          defaultActiveRoundTargets: defaultActiveRoundTargets.filter(i => i !== roundTarget.roundNumber),
        });
      })
      .catch(() => { });
  }

  hideError = () => {
    this.setState({
      showError: false
    });
  };

  render() {
    const {
      loaded,
      roundTargets,
      changedRoundTargets,
      showError,
      errorTitle,
      errorMessage,
      showSuccess,
      successMessage,
      defaultActiveRoundTargets,
    } = this.state;

    return loaded ? (
      <Wrapper>
        {showError && (
          <ErrorAlert
            title={errorTitle}
            message={errorMessage}
            onHide={this.hideError}
          />
        )}
        <Title>ROUND TARGETS</Title>
        <Text>Each round of the simulation comes with financial targets. Teams earn a round score of 100% when every targeted metric is hit. The team's overall team score is the weighted average of all round scores (round weights live on the Scoring Weights page).</Text>
        <br />
        <Text>Note that a few financial metrics are only relevant in certain rounds. </Text>
        {showSuccess && (
          <SuccessAlert
            title="Success!"
            message={successMessage}
            onHide={this.hideError}
          />
        )}
        <DataContainer>
          <DataContainerTitle>Modify Round Targets</DataContainerTitle>
          <table>
            <thead>
              <tr>
                <HeaderColumn>Round</HeaderColumn>
                <HeaderColumn>Market<br /> Share</HeaderColumn>
                <HeaderColumn>Revenues</HeaderColumn>
                <HeaderColumn>Gross<br /> Margins</HeaderColumn>
                <HeaderColumn>Share<br /> Price</HeaderColumn>
                <HeaderColumn>ROE</HeaderColumn>
                <HeaderColumn>ROA</HeaderColumn>
                <HeaderColumn>EPS</HeaderColumn>
              </tr>
            </thead>
            <tbody>
              {
                roundTargets.map((roundTargets, index) => {
                  const isIAS = Auth.isAssistantOrCoInstructor();
                  const isDefault = defaultActiveRoundTargets.includes(roundTargets.roundNumber);
                  const roundTargetsButtonActive = !isIAS && (isDefault || changedRoundTargets.includes(roundTargets.roundNumber) ? true : false);
                  const roundTargetsButtonText = isDefault
                    ? 'Activate'
                    : changedRoundTargets.includes(roundTargets.roundNumber)
                      ? 'Update'
                      : 'Updated';

                  const endDate = getDateInCurrentTimezone(
                    moment(roundTargets.roundEndDate)
                  );
                  const nowDate = getDateInCurrentTimezone(moment());
                  const inputDisabled = isIAS || nowDate > endDate;

                  return (
                    <tr key={index}>
                      <TableCell>{roundTargets.roundNumber}</TableCell>
                      <TableCell verticalPadding active={Number(roundTargets.marketShare) > 0}>
                        <AbbreviateNumber
                          type="input"
                          value={roundTargets.marketShare}
                          onChange={(e, value) =>
                            this.handleRoundsTargetsInputChange(
                              e,
                              value,
                              roundTargets.roundNumber,
                              'marketShare'
                            )
                          }
                          prefix=""
                          suffix="%"
                          disabled={inputDisabled}
                        />
                      </TableCell>
                      <TableCell verticalPadding active={Number(roundTargets.netRevenues) > 0}>
                        <AbbreviateNumber
                          type="input"
                          value={roundTargets.netRevenues}
                          onChange={(e, value) =>
                            this.handleRoundsTargetsInputChange(
                              e,
                              value,
                              roundTargets.roundNumber,
                              'netRevenues'
                            )
                          }
                          suffix="M"
                          disabled={inputDisabled}
                        />
                      </TableCell>
                      <TableCell verticalPadding active={Number(roundTargets.grossMargin) > 0}>
                        <AbbreviateNumber
                          type="input"
                          value={roundTargets.grossMargin}
                          onChange={(e, value) =>
                            this.handleRoundsTargetsInputChange(
                              e,
                              value,
                              roundTargets.roundNumber,
                              'grossMargin'
                            )
                          }
                          prefix=""
                          suffix="%"
                          disabled={inputDisabled}
                        />
                      </TableCell>
                      <TableCell verticalPadding active={Number(roundTargets.stockPrice) > 0}>
                        <AbbreviateNumber
                          type="input"
                          value={roundTargets.stockPrice}
                          onChange={(e, value) =>
                            this.handleRoundsTargetsInputChange(
                              e,
                              value,
                              roundTargets.roundNumber,
                              'stockPrice'
                            )
                          }
                          disabled={inputDisabled}
                        />
                      </TableCell>
                      <TableCell verticalPadding active={Number(roundTargets.roe) > 0}>
                        <AbbreviateNumber
                          type="input"
                          value={roundTargets.roe}
                          onChange={(e, value) =>
                            this.handleRoundsTargetsInputChange(
                              e,
                              value,
                              roundTargets.roundNumber,
                              'roe'
                            )
                          }
                          prefix=""
                          suffix="%"
                          disabled={inputDisabled}
                        />
                      </TableCell>
                      <TableCell verticalPadding active={Number(roundTargets.roa) > 0}>
                        <AbbreviateNumber
                          type="input"
                          value={roundTargets.roa}
                          onChange={(e, value) =>
                            this.handleRoundsTargetsInputChange(
                              e,
                              value,
                              roundTargets.roundNumber,
                              'roa'
                            )
                          }
                          prefix=""
                          suffix="%"
                          disabled={inputDisabled}
                        />
                      </TableCell>
                      <TableCell verticalPadding active={Number(roundTargets.eps) > 0}>
                        <AbbreviateNumber
                          type="input"
                          value={roundTargets.eps}
                          onChange={(e, value) =>
                            this.handleRoundsTargetsInputChange(
                              e,
                              value,
                              roundTargets.roundNumber,
                              'eps'
                            )
                          }
                          disabled={inputDisabled}
                        />
                      </TableCell>
                      <TableCell verticalPadding>
                        <Button
                          type="button"
                          disabled={!roundTargetsButtonActive}
                          isActiveDefault={!isIAS && isDefault}
                          onClick={() => this.submitRoundTarget(roundTargets)}
                        >
                          {roundTargetsButtonText}
                        </Button>
                      </TableCell>
                    </tr>
                  )
                })
              }
            </tbody>
          </table>
        </DataContainer>
      </Wrapper>
    ) : <Wrapper><Loader type="ball-scale-ripple" active /></Wrapper>;
  }

}

export default TeamScore;



// WEBPACK FOOTER //
// ./src/instructor/components/grading/pages/team-score.js